import { Injectable } from '@angular/core';

import { ConsentDialogManager } from '@guardfinder/analytics';

import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
/**
 * Service that manages the visibility state of the analytics consent modal.
 * Provides methods to show and hide the consent dialog, and an observable
 * to track the current visibility state.
 */
export class ObservableConsentDialogManager extends ConsentDialogManager {
    /**
     * BehaviorSubject that holds and emits the current visibility state of the consent dialog.
     * Initialized as false (not shown).
     */
    private readonly isConsentShown = new BehaviorSubject<boolean>(false);

    /**
     * Creates an instance of ObservableConsentDialogManager.
     * Initializes the base class and the isConsentShown subject.
     */
    constructor() {
        super();
    }

    /**
     * Gets the current visibility state of the consent dialog as an Observable.
     * This allows components to reactively respond to visibility changes.
     *
     * @returns An Observable that emits the current visibility state and any future changes
     */
    override getConsent(): Observable<boolean> {
        return this.isConsentShown.asObservable();
    }

    /**
     * Shows the consent dialog by updating the visibility state to true.
     * Components subscribed to getConsent() will be notified of this change.
     *
     * @returns A Promise that resolves when the visibility state has been updated
     */
    async presentConsent(): Promise<void> {
        this.isConsentShown.next(true);
    }

    /**
     * Hides the consent dialog by updating the visibility state to false.
     * Components subscribed to getConsent() will be notified of this change.
     *
     * @returns A Promise that resolves when the visibility state has been updated
     */
    async hideConsent(): Promise<void> {
        this.isConsentShown.next(false);
    }
}
